import React, { Dispatch } from "react";
import { RouteComponentProps } from "react-router-dom";
import { showSnack } from "react-redux-snackbar";
import { connect } from "react-redux";
import {
  LoadingState,
  ResponseError,
  ErrorDisplay,
  LoadingDisplay,
  ApiRequest,
  ForbiddenDisplay
} from "../../../Common";
import { endpoints } from "../../Constants/endpoints";
import ResponseHandler from "../../../App/ResponseHandler";
import { ResponseResult } from "../../../Common/Models/ResponseResult";
import { StatusCode } from "../../../App/Enums/StatusCode";
import { SettingsContainer } from "../../../Layout";
import { CompanySettings } from "../../Models/CompanySettings";
import { PrivacySettings } from "./PrivacySettings";
import { CompanySettingCategory } from "../../Constants/CompanySettingCategory";

type TParams = {
  shortname: string;
};

type State = {
  settings?: CompanySettings;
  settingCategories: CompanySettingCategory[];
  selectedCategory: CompanySettingCategory;
  loadingState: LoadingState;
  loadingError: ResponseError;
};

type DispatchProps = {
  showSnack: (uniqueId: any, args: any) => void;
};

const mapDispatchToProps = (dispatch: Dispatch<any>) => ({
  showSnack: (uniqueId: any, args: any) => dispatch(showSnack(uniqueId, args))
});

const initialState = {
  settingCategories: [CompanySettingCategory.PrivacySettings],
  selectedCategory: CompanySettingCategory.PrivacySettings,
  loadingState: {
    errored: false,
    isLoaded: false,
    isLoading: false
  },
  loadingError: {
    errorDescription: "",
    errorResult: ""
  },
  settings: undefined
};

class CompanySettingsContainer extends React.Component<
  RouteComponentProps<TParams> & DispatchProps,
  State
> {
  constructor(props: RouteComponentProps<TParams> & DispatchProps) {
    super(props);

    this.state = initialState;
  }

  componentDidMount = () => {
    this.getCompanySettings(this.props.match.params.shortname);
  };

  componentDidUpdate = (prevProps: RouteComponentProps<TParams> & DispatchProps) => {
    if (prevProps.match.params.shortname !== this.props.match.params.shortname) {
      this.setState(initialState, () => this.getCompanySettings(this.props.match.params.shortname));
    }
  };

  getCompanySettings = (shortname: string) => {
    ApiRequest.fromPath(endpoints.settings.getSettings(shortname))
      .withAuthHeader()
      .get()
      .then(response => {
        ResponseHandler.handle(
          response,
          (result: ResponseResult<CompanySettings>) => {
            const prevLoadingState = this.state.loadingState;

            this.setState({
              settings: result.payload,
              loadingState: {
                ...prevLoadingState,
                ...({
                  isLoaded: true,
                  isLoading: false
                } as LoadingState)
              }
            });
          },
          result => {
            this.setLoadingError(response.status, result.error.message, "Failed to get settings");
          },
          () => {
            this.setLoadingError(
              response.status,
              "An unknown error has occured",
              "Failed to get settings"
            );
          }
        );
      })
      .catch(() => {
        this.setLoadingError(
          StatusCode.Status503ServiceUnavailable,
          "Unable to connect to server",
          "Failed to get settings"
        );
      });
  };

  setCompanySetting = (key: string, value: string, action: () => void) => {
    ApiRequest.fromPath(endpoints.settings.setSetting(this.props.match.params.shortname))
      .withAuthHeader()
      .withJson({ key, value })
      .put()
      .then(response => {
        ResponseHandler.handle(
          response,
          () => {
            action();
          },
          result => {
            this.setLoadingError(
              response.status,
              result.error.message,
              "Failed to set setting",
              true
            );
          },
          () => {
            this.setLoadingError(
              response.status,
              "An unknown error has occured",
              "Failed to set setting",
              true
            );
          }
        );
      })
      .catch(() => {
        this.setLoadingError(
          StatusCode.Status503ServiceUnavailable,
          "Unable to connect to server",
          "Failed to get settings",
          true
        );
      });
  };

  setLoadingError = (
    errorCode: number,
    errorDescription: string,
    errorResult: string,
    shouldShowSnack: boolean = false
  ) => {
    const prevLoadingState = this.state.loadingState;

    this.setState(
      {
        loadingState: {
          ...prevLoadingState,
          ...({
            errored: !shouldShowSnack,
            isLoading: false
          } as LoadingState)
        },
        loadingError: {
          errorCode,
          errorDescription,
          errorResult
        }
      },
      () => {
        if (shouldShowSnack) {
          this.showErrorSnackbar(errorCode, errorDescription, errorResult);
        }
      }
    );
  };

  onSettingCategoryClick = (category: CompanySettingCategory) => {
    this.setState({
      selectedCategory: category
    });
  };

  showErrorSnackbar = (errorCode: number, errorDescription: string, reason: string) => {
    this.props.showSnack("FEEDBACK_CREATE_BUG_REPORT", {
      label: `${reason}: ${errorDescription} (Error Code ${errorCode})`,
      timeout: 2500
    });
  };

  render() {
    if (this.state.loadingState.errored) {
      if (this.state.loadingError.errorCode === StatusCode.Status403Forbidden) {
        return <ForbiddenDisplay description={this.state.loadingError.errorDescription} />;
      }

      return <ErrorDisplay {...this.state.loadingError} />;
    }

    if (this.state.loadingState.isLoaded && !!this.state.settings) {
      let settingPage;

      switch (this.state.selectedCategory) {
        case CompanySettingCategory.PrivacySettings:
          settingPage = (
            <PrivacySettings settings={this.state.settings} onSetSetting={this.setCompanySetting} />
          );
          break;
        default:
          return (
            <ErrorDisplay
              errorResult="Failed to load settings"
              errorDescription={`${this.state.selectedCategory} is not a valid setting category`}
            />
          );
      }

      return (
        <SettingsContainer
          title="Company Settings"
          selectedCategory={this.state.selectedCategory}
          settingCategories={this.state.settingCategories}
          onCategoryChange={this.onSettingCategoryClick}
        >
          {settingPage}
        </SettingsContainer>
      );
    }
    return <LoadingDisplay />;
  }
}

export default connect(
  null,
  mapDispatchToProps
)(CompanySettingsContainer);
